import { ActionIcon, Menu, Tooltip } from '@mantine/core';
import IconFocusCentered from '@tabler/icons-react/dist/esm/icons/IconFocusCentered.mjs';
import IconJson from '@tabler/icons-react/dist/esm/icons/IconJson.mjs';
import IconPng from '@tabler/icons-react/dist/esm/icons/IconPng.mjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionsMenu } from '@/shared/ui/actions-menu';
import { messageError } from '@/shared/ui/notifications';
import { exportFile } from '@/shared/lib/utils/export-file';
import { GraphControlsPortal } from './controls';
import { useGraphRendererApi } from '../model/use-api';

export const RecenterControl = () => {
  const { t } = useTranslation();
  const getApi = useGraphRendererApi();
  return (
    <GraphControlsPortal>
      <Tooltip label={t('graphRenderer.action.recenter')}>
        <ActionIcon
          data-testid='graph-recenter'
          variant='light'
          onClick={() => getApi()?.fit()}
        >
          <IconFocusCentered />
        </ActionIcon>
      </Tooltip>
    </GraphControlsPortal>
  );
};

export const ExportJSON = () => {
  const { t } = useTranslation();
  const getApi = useGraphRendererApi();
  return (
    <GraphControlsPortal>
      <Tooltip label={t('graphRenderer.export.json')}>
        <ActionIcon
          data-testid='graph-export-json'
          variant='light'
          onClick={() => {
            const api = getApi();
            if (!api) return;
            const nodes = api
              .getNodes()
              .map(({ data, position, classes, selectable }) => ({
                data,
                position,
                classes,
                selectable,
              }));
            const edges = api
              .getEdges()
              .map(({ data, classes, selectable }) => ({
                data,
                classes,
                selectable,
              }));
            exportFile(
              JSON.stringify({ nodes, edges }),
              'application/json',
              'graph.json',
            );
          }}
        >
          <IconJson />
        </ActionIcon>
      </Tooltip>
    </GraphControlsPortal>
  );
};

export const ExportPNG = () => {
  const { t } = useTranslation();
  const getApi = useGraphRendererApi();
  const [pending, setPending] = useState(false);
  const save = async (full: boolean) => {
    const api = getApi();
    if (!api || pending) return;
    setPending(true);
    try {
      exportFile(
        await api.exportPNG({ full, scale: 2 }),
        'image/png',
        'graph.png',
      );
    } catch (error) {
      messageError(error instanceof Error ? error.message : String(error));
    } finally {
      setPending(false);
    }
  };
  return (
    <GraphControlsPortal>
      <ActionsMenu
        data-testid='graph-export-png'
        icon={<IconPng />}
        position='right-start'
        actionIconProps={{
          size: 'md',
          variant: 'light',
          loading: pending,
          'data-testid': 'graph-export-png',
        }}
      >
        <Menu.Item
          data-testid='graph-export-png-viewport'
          disabled={pending}
          onClick={() => void save(false)}
        >
          {t('graphRenderer.export.viewport')}
        </Menu.Item>
        <Menu.Item
          data-testid='graph-export-png-graph'
          disabled={pending}
          onClick={() => void save(true)}
        >
          {t('graphRenderer.export.graph')}
        </Menu.Item>
      </ActionsMenu>
    </GraphControlsPortal>
  );
};